import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useMatrixParams } from '@/app/MatrixSwitch';
import { NotificationTextMessage } from '@/core/enums/notification';
import { isGuid, toIsoDate, type ClientPeriods } from '@/core/models/practice.models';
import {
  TrialBalanceType,
  ensureTrailingRows,
  findAccount,
  linesWithCode,
  recalculateTrialBalance,
  toEditLines,
  toLineInputs,
  toPeriodOptions,
  type ChartAccountOption,
  type PeriodOption,
  type SaveTrialBalancePayload,
  type TrialBalanceDetail,
  type TrialBalanceEditModel,
} from '@/core/models/trial-balance.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { matrixPath } from '@/core/routing/matrix';
import { selectedClient } from '@/core/selectedClient';
import { practiceService } from '@/core/services/practice';
import { trialBalanceService } from '@/core/services/trialBalance';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { SectionTabs } from '@/kit/SectionTabs';
import { openDialog } from '@/kit/dialog';
import { AddNewAccount } from './AddNewAccount';
import { Confirmation } from './Confirmation';
import { ImportTrailBalance } from './ImportTrailBalance';
import { TrialBalanceActions } from './TrialBalanceActions';
import { TrialBalanceBackdetails } from './TrialBalanceBackdetails';
import { TrialBalanceDetails } from './TrialBalanceDetails';
import { TrialBalanceOverview, TrialBalanceSummary } from './TrialBalanceOverview';
import './TrialBalanceEditor.scss';
import { NO_LINES_MESSAGE, askForTrialBalanceDetails, useTrialBalanceDraft } from './trialBalanceDraft';
import {
  ModelHolder,
  applyImportedLines,
  applyPosted,
  applySavedDetail,
  focusedAmountLine,
  type EditModel,
  TB_EDIT_BASE,
  TB_EDIT_SEGMENT,
  TB_LIST_ROUTE,
  applyServerTotals,
  applyChartAccounts,
  loadChartAccounts,
  newAccountPrefill,
} from './trialBalanceEditor';

/** BUG-036: what an import says - the lines are in the grid, not stored yet. */
const IMPORTED_UNSAVED_MESSAGE =
  'Balances imported from the selected trial balance. Save the trial balance to keep them.';

function buildPayload(model: TrialBalanceEditModel): SaveTrialBalancePayload {
  return {
    clientId: model.clientId,
    id: model.id,
    refNo: model.refNo.trim(),
    description: model.description?.trim() ? model.description.trim() : null,
    typeId: Number(model.typeId) || 0,
    importModeId: Number(model.importModeId),
    accountingPeriodId: model.accountingPeriodId || null,
    fromDate: toIsoDate(model.fromDate),
    toDate: toIsoDate(model.toDate),
    lines: toLineInputs(model.lines),
  };
}

/** Angular `Location.replaceState`: swaps the current history entry's URL without routing (must never throw). */
function replaceUrl(url: string) {
  try {
    window.history.replaceState(window.history.state, '', url);
  } catch {
    // nothing to rewrite (restricted history); the save itself already succeeded
  }
}

/** A period-load failure is toasted but does not stop the page (the period list is then empty). */
async function periodsFor(clientId: string): Promise<ClientPeriods | null> {
  try {
    return await practiceService.getClientPeriods(clientId);
  } catch (error) {
    notifyError(error);
    return null;
  }
}

/**
 * Angular TaxreturnTaskCreatetrialbalanceComponent: the trial balance editor. It owns ONE shared mutable model that the
 * three panels (Overview, Edit Details, the lines grid) read and mutate in place. BUG-036: as the old application's
 * journal grid, nothing is stored while it is edited (no autosave). Save stores it - only when it is balanced - posts
 * an annual one to its accounting period and returns to the list; Cancel, the X and leaving any other way store
 * nothing. `revision` is bumped on every change so the panels refresh even though the model object never changes.
 */
export function TaxreturnTaskCreatetrialbalance() {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  // Angular read `route.snapshot.paramMap` once: a later change of the matrix param (the URL swap below) is ignored.
  const [encodedId] = useState(useMatrixParams().id);
  // BUG-037: the details confirmed in Create Trial Balance (null when the editor was opened without them).
  const draftRef = useTrialBalanceDraft();

  const [holder] = useState(() => new ModelHolder());
  const [periods, setPeriods] = useState<PeriodOption[]>([]);
  const [activeTab, setActiveTab] = useState<'overview' | 'editdetails'>('overview');
  /** Bumped on every change so display-only panels can refresh. */
  const [revision, setRevision] = useState(0);
  /** BUG-036: a Save (and the post that follows it) is in progress. */
  const [isSaving, setIsSavingState] = useState(false);
  const isSavingRef = useRef(false);
  /** BUG-036: there are changes Save has not stored yet. */
  const dirtyRef = useRef(false);
  /** A load failure is toasted and redirected once (React StrictMode runs the load twice in development). */
  const loadFailed = useRef(false);

  const bump = useCallback(() => setRevision((r) => r + 1), []);
  const setSaving = (value: boolean) => {
    isSavingRef.current = value;
    setIsSavingState(value);
  };

  /**
   * Stores the header and full line set when there are unsaved changes (or the trial balance does not exist yet).
   * Only Save calls it (BUG-036); a failure is always shown.
   */
  const saveNow = async (): Promise<boolean> => {
    const model = holder.current;
    if (!model) {
      return false;
    }
    if (!dirtyRef.current && model.id) {
      return true;
    }

    const payload = buildPayload(model);
    dirtyRef.current = false;
    try {
      const detail = await trialBalanceService.saveTrialBalance(payload);
      const created = !model.id;
      // BUG-039: the cells show the stored (normalised) amounts, except one still being typed in.
      applySavedDetail(model, detail, dirtyRef.current, focusedAmountLine());
      bump();
      // Angular `location.replaceState(...)`: the URL is rewritten to the edit URL of the created record without
      // routing, so a refresh (e.g. after a failed post) reloads it.
      if (created) {
        replaceUrl(matrixPath(TB_EDIT_BASE, TB_EDIT_SEGMENT, { id: btoa(detail.id) }));
      }
      return true;
    } catch (error) {
      dirtyRef.current = true;
      notifyError(error);
      return false;
    }
  };

  useEffect(() => {
    navigateRef.current = navigate;
  });

  // BUG-036: leaving the page (menu, link, back) stores nothing - there is no unmount save; only Save stores.

  // ngOnInit
  useEffect(() => {
    let cancelled = false;
    const goTo = (path: string) => navigateRef.current(path);
    const failLoad = (error: unknown) => {
      if (loadFailed.current) return;
      loadFailed.current = true;
      notifyError(error);
      goTo(TB_LIST_ROUTE);
    };

    /** A saved trial balance, as loaded (or as a Bookkeeping import just created it - BUG-038). */
    const present = (detail: TrialBalanceDetail, clientPeriods: ClientPeriods | null) => {
      setPeriods(clientPeriods ? toPeriodOptions(clientPeriods.periods) : []);
      const model: TrialBalanceEditModel = {
        id: detail.id,
        clientId: detail.clientId,
        clientName: detail.clientName,
        refNo: detail.refNo,
        description: detail.description ?? '',
        typeId: detail.typeId,
        importModeId: detail.importModeId,
        accountingPeriodId: detail.accountingPeriodId,
        periodLabel: detail.periodLabel,
        fromDate: detail.fromDate,
        toDate: detail.toDate,
        lines: ensureTrailingRows(toEditLines(detail.lines)),
        accounts: detail.accounts ?? [],
        totalDebit: 0,
        totalCredit: 0,
        difference: 0,
        isBalanced: true,
        profit: 0,
        isPosted: detail.isPosted,
        postedAtUtc: detail.postedAtUtc,
        isUsedByReport: detail.isUsedByReport ?? false,
      };
      recalculateTrialBalance(model);
      applyServerTotals(model, detail);
      holder.set(model);
      bump();
    };

    const startNew = async () => {
      const client = selectedClient();
      if (!client) {
        notifyError('Select a client from Accounts first.');
        goTo('/home/accountclient');
        return;
      }
      let refNo: string;
      let clientPeriods: ClientPeriods | null;
      let accounts: ChartAccountOption[];
      try {
        [refNo, clientPeriods, accounts] = await Promise.all([
          trialBalanceService.getNextRefNo(client.id),
          periodsFor(client.id),
          // #193: the Account picker offers the chart from the first line, not only once a save has brought it back.
          // Without it the trial balance can still be entered; the chart then arrives with the first save.
          loadChartAccounts(client.id).catch((error: unknown) => {
            notifyError(error);
            return [];
          }),
        ]);
      } catch (error) {
        // Angular never unsubscribed: a load error after the page was left still toasts and redirects.
        failLoad(error);
        return;
      }
      if (cancelled) return;
      const options = clientPeriods ? toPeriodOptions(clientPeriods.periods) : [];
      setPeriods(options);
      // BUG-037: the Period, Mode of Import, Ref. No. and Description are the ones confirmed in Create Trial Balance -
      // never preset. Opened without them (the header's "New trial balance", a link), the editor asks for them; Cancel
      // goes back to the list with nothing created.
      let draft = draftRef.current;
      if (!draft) {
        const result = await askForTrialBalanceDetails(client.id);
        if (cancelled) return;
        if (result && 'created' in result) {
          // BUG-038: a Bookkeeping import created it - it opens as saved, under its own URL.
          replaceUrl(matrixPath(TB_EDIT_BASE, TB_EDIT_SEGMENT, { id: btoa(result.created.id) }));
          present(result.created, clientPeriods);
          return;
        }
        draft = result?.draft ?? null;
      }
      if (!draft) {
        goTo(TB_LIST_ROUTE);
        return;
      }
      draftRef.current = draft;
      const model: TrialBalanceEditModel = {
        id: null,
        clientId: client.id,
        clientName: clientPeriods?.clientName || client.name,
        refNo: draft.refNo || refNo,
        description: draft.description ?? '',
        typeId: draft.typeId,
        importModeId: draft.importModeId,
        accountingPeriodId: draft.accountingPeriodId,
        periodLabel: draft.periodLabel || options.find((o) => o.id === draft.accountingPeriodId)?.label || '',
        fromDate: draft.fromDate,
        toDate: draft.toDate,
        // BUG-036: the lines read from the file chosen in Create Trial Balance open unsaved, for Save to store.
        lines: ensureTrailingRows(toEditLines(draft.lines ?? [])),
        accounts,
        totalDebit: 0,
        totalCredit: 0,
        difference: 0,
        isBalanced: true,
        profit: 0,
        isPosted: false,
        postedAtUtc: null,
        isUsedByReport: false,
      };
      recalculateTrialBalance(model);
      holder.set(model);
      dirtyRef.current = (draft.lines?.length ?? 0) > 0;
      bump();
    };

    const loadExisting = async (id: string) => {
      let detail: TrialBalanceDetail;
      let clientPeriods: ClientPeriods | null;
      try {
        detail = await trialBalanceService.getTrialBalance(id);
        clientPeriods = await periodsFor(detail.clientId);
      } catch (error) {
        // Angular never unsubscribed: a load error after the page was left still toasts and redirects.
        failLoad(error);
        return;
      }
      if (cancelled) return;
      present(detail, clientPeriods);
    };

    if (!encodedId) {
      void startNew();
    } else {
      let id: string | null;
      try {
        id = atob(encodedId);
      } catch {
        id = null;
      }
      if (!isGuid(id)) {
        notifyError('The trial balance link is not valid.');
        goTo(TB_LIST_ROUTE);
      } else {
        void loadExisting(id!);
      }
    }
    return () => {
      cancelled = true;
    };
  }, [encodedId, bump, holder, draftRef]);

  // ---- Edits (BUG-036: kept on screen until Save) ----

  /** Called by the panels after any edit: recalculate, refresh and mark the trial balance unsaved. */
  const onModelChanged = () => {
    const model = holder.current;
    if (!model) {
      return;
    }
    recalculateTrialBalance(model);
    bump();
    dirtyRef.current = true;
  };

  /** How the panels change the shared model (Angular: they mutated it, then emitted `modelChange`). */
  const edit: EditModel = (mutate) => {
    const model = holder.current;
    if (model) {
      mutate(model);
    }
    onModelChanged();
  };

  // ---- Actions ----

  /**
   * The old grid's Save: a trial balance is stored whatever state it is in - balanced or not (the old app does the
   * same; it is only posting that requires balance). A new one is created with its lines (BUG-037); an annual one
   * with an accounting period is then staged into it, same as a Capium Bookkeeping import, and posted immediately
   * when it already balances - otherwise it is left for Import Exceptions to review and commit with Resume Import.
   * Either way the list opens again once Save finishes.
   */
  const onSave = async () => {
    const model = holder.current;
    if (!model || isSavingRef.current) {
      return;
    }
    if (!model.refNo?.trim()) {
      notifyError('Enter the trial balance reference.');
      return;
    }
    // BUG-037: a new trial balance is never created as an empty record.
    if (!model.id && linesWithCode(model).length === 0) {
      notifyError(NO_LINES_MESSAGE);
      return;
    }

    setSaving(true);
    const saved = await saveNow();
    if (!saved || !model.id) {
      setSaving(false);
      return;
    }

    const canStage =
      Number(model.typeId) === TrialBalanceType.Annual && !!model.accountingPeriodId && linesWithCode(model).length > 0;

    if (canStage) {
      try {
        const detail = await trialBalanceService.postTrialBalance(model.id);
        applyPosted(model, detail);
        // APT334: a balanced trial balance that was not posted was held for another reason (a line on an account the chart does not
        // have, an import identical to the live one) and the server says which; "it does not balance" is only for one that does not.
        // APT334 (the client's answer: "only that particular line should wait"): a trial balance posted EXCEPT for a line held on Import
        // Exceptions says so - which line, why, and how far out the adjusted trial balance is - instead of "saved and posted".
        notifySuccess(
          detail.isPosted
            ? detail.postMessage || 'Trial balance saved and posted to the accounting period'
            : detail.isBalanced && detail.postMessage
              ? detail.postMessage
              : 'Saved as a draft: it does not balance. Correct the lines here and Save again, or review it on Import Exceptions.',
        );
      } catch (error) {
        notifyError(error);
        setSaving(false);
        return;
      }
    } else {
      notifySuccess('Trial balance saved');
    }
    navigateRef.current(TB_LIST_ROUTE);
  };

  /** BUG-036: the old grid's Cancel (and the X): back to the list; changes not saved are discarded. */
  const onCancel = () => {
    if (isSavingRef.current) {
      return;
    }
    dirtyRef.current = false;
    navigateRef.current(TB_LIST_ROUTE);
  };

  /** Lines already entered are replaced; ask first. */
  const confirmReplaceLines = async (): Promise<boolean> => {
    const model = holder.current;
    if (!model || linesWithCode(model).length === 0) {
      return true;
    }
    const confirmed = await openDialog<undefined, boolean>(Confirmation).afterClosed();
    return confirmed === true;
  };

  /**
   * BUG-040: Chart of Accounts > Add New Account, opened from the trial balance, so a missing nominal is added without
   * leaving it. The chart the Account picker offers is then reloaded; the new account's code is returned when it can be
   * picked (an inactive account is saved but not offered), for the line it was added from to take.
   */
  const onAddAccount = async (searchTerm: string): Promise<string | null> => {
    const model = holder.current;
    if (!model) {
      return null;
    }

    let options;
    try {
      options = await trialBalanceService.getAccountOptions(model.clientId);
    } catch (error) {
      notifyError(error);
      return null;
    }

    const saved = await openDialog(AddNewAccount, {
      data: { clientId: model.clientId, options, prefill: newAccountPrefill(searchTerm) },
      width: '580px',
      panelClass: 'dialog-align-top',
    }).afterClosed();
    if (!saved) {
      return null;
    }
    notifySuccess(NotificationTextMessage.successMessage);

    try {
      applyChartAccounts(model, await loadChartAccounts(model.clientId));
    } catch (error) {
      notifyError(error);
      return null;
    }
    bump();
    return findAccount(model.accounts, saved.code)?.code ?? null;
  };

  const onImportBalances = async () => {
    const model = holder.current;
    if (!model) {
      return;
    }

    let options;
    try {
      options = (await trialBalanceService.getTrialBalances(model.clientId)).filter((r) => r.id !== model.id);
    } catch (error) {
      notifyError(error);
      return;
    }

    const result = await openDialog(ImportTrailBalance, {
      data: { options, targetRefNo: model.refNo },
    }).afterClosed();
    if (!result?.sourceTrialBalanceId || !(await confirmReplaceLines())) {
      return;
    }

    // BUG-036: the carried balances replace the lines in the grid as unsaved changes - nothing is stored until Save,
    // which stores them only once they balance (a new trial balance is created by that Save, BUG-037).
    let lines;
    try {
      lines = await trialBalanceService.previewCarryForward(model.clientId, result);
    } catch (error) {
      notifyError(error);
      return;
    }
    edit((m) => applyImportedLines(m, lines));
    notifySuccess(IMPORTED_UNSAVED_MESSAGE);
  };

  const model = holder.current;

  return (
    <SideMenuPage menu="task">
      <div className="sidenav-details">
        <div className="sidenav-body">
          <SectionTabs
            ariaLabel="Create Trial Balance sections"
            value={activeTab}
            onChange={(id) => setActiveTab(id as 'overview' | 'editdetails')}
            tabs={[
              { id: 'overview', label: 'Overview', infoText: 'The imported trial balance figures and their mapped accounts.' },
              { id: 'editdetails', label: 'Edit Details', infoText: 'Change this trial balance’s name, description and period.' },
            ]}
          >
            {(active) =>
              active === 'overview'
                ? model && (
                    <TrialBalanceOverview
                      model={model}
                      revision={revision}
                      onImportBalances={() => void onImportBalances()}
                    />
                  )
                : model && <TrialBalanceDetails model={model} periods={periods} onEdit={edit} />
            }
          </SectionTabs>

          {/* The summary belongs to the Overview - Edit Details only edits the name, description and period. */}
          {model && activeTab === 'overview' && <TrialBalanceSummary model={model} revision={revision} />}

          {/*
            The lines grid belongs to the Overview. It stays mounted (hidden) on Edit Details so what the grid has worked out
            about the lines (accounts picked, particulars filled in) is not lost when the user goes there and back.
          */}
          {model && (
            <div hidden={activeTab !== 'overview'}>
              <TrialBalanceBackdetails
                model={model}
                revision={revision}
                onEdit={edit}
                onOpenChartOfAccounts={() => navigate('/home/taxreturns/settings-accoununts')}
                onAddAccount={onAddAccount}
                onSave={activeTab === 'overview' ? () => void onSave() : undefined}
                onCancel={activeTab === 'overview' ? onCancel : undefined}
                isSaving={isSaving}
              />
            </div>
          )}
          {model && activeTab === 'editdetails' && (
            <TrialBalanceActions onSave={() => void onSave()} onCancel={onCancel} isSaving={isSaving} />
          )}
        </div>
      </div>
    </SideMenuPage>
  );
}
